import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type { UnitMapAt } from '@/Network/Types/Responses/UnitMapResponses'

/**
 * Estado do Mapa da unidade na URL (spec 2.4 §5.2): `unidade=<id>`,
 * `momento=agora|proximo-turno|amanha-07`, `especialidades=A&especialidades=B` e `tv=1`.
 * Nenhuma dessas chaves vira filtro da API de `GET /stations`.
 *
 * Especialidades vão em chave repetida (nomes com vírgula sobrevivem ao reload). O leitor
 * ainda aceita a forma antiga separada por vírgula quando vem uma chave só.
 */
export type UnitMapQueryState = {
  healthUnitId: number | null
  at: UnitMapAt
  specialties: string[]
  tv: boolean
}

export const DEFAULT_UNIT_MAP_QUERY_STATE: Readonly<UnitMapQueryState> = Object.freeze({
  healthUnitId: null,
  at: 'now',
  specialties: [],
  tv: false
})

export const UNIT_MAP_QUERY_KEYS: readonly string[] = ['unidade', 'momento', 'especialidades', 'tv']

const MOMENT_TO_AT: Record<string, UnitMapAt> = {
  agora: 'now',
  'proximo-turno': 'next_shift',
  'amanha-07': 'tomorrow_07'
}

const AT_TO_MOMENT: Record<UnitMapAt, string> = {
  now: 'agora',
  next_shift: 'proximo-turno',
  tomorrow_07: 'amanha-07'
}

const firstValue = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null)

function parseUnitId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const id = Number(raw)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

/**
 * Chave repetida (`especialidades=A&especialidades=B`): cada valor é um nome inteiro, com
 * vírgula ou não. Chave única: forma antiga, separada por vírgula.
 */
function parseSpecialties(value: LocationQueryValue | LocationQueryValue[] | undefined): string[] {
  const repeated = Array.isArray(value)
  const raw = (repeated ? value : [value]).filter(
    (item): item is string => typeof item === 'string'
  )
  const names = (repeated ? raw : raw.flatMap((item) => item.split(',')))
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
  return [...new Set(names)]
}

export function parseUnitMapQuery(query: LocationQuery): UnitMapQueryState {
  const moment = firstValue(query.momento)
  return {
    healthUnitId: parseUnitId(firstValue(query.unidade)),
    at: (moment && MOMENT_TO_AT[moment]) || DEFAULT_UNIT_MAP_QUERY_STATE.at,
    specialties: parseSpecialties(query.especialidades),
    tv: firstValue(query.tv) === '1'
  }
}

export type UnitMapSerializedQuery = Record<string, string | string[]>

/**
 * Especialidades em chave repetida. Um nome só, sem vírgula, vira chave simples; um nome só
 * COM vírgula ganha uma chave vazia extra (`especialidades=A, B&especialidades=`) para
 * continuar repetida no reload e não cair na leitura antiga por vírgula.
 */
function serializeSpecialties(names: string[]): string | string[] {
  if (names.length > 1) return [...names]
  const [only] = names
  return only.includes(',') ? [only, ''] : only
}

/** Só grava o que difere do padrão (URL limpa em "Agora", sem filtro e fora do Modo TV). */
export function serializeUnitMapQuery(state: UnitMapQueryState): UnitMapSerializedQuery {
  const out: UnitMapSerializedQuery = {}
  if (state.healthUnitId !== null) out.unidade = String(state.healthUnitId)
  if (state.at !== DEFAULT_UNIT_MAP_QUERY_STATE.at) out.momento = AT_TO_MOMENT[state.at]
  if (state.specialties.length > 0) out.especialidades = serializeSpecialties(state.specialties)
  if (state.tv) out.tv = '1'
  return out
}

const asList = (value: unknown): unknown[] | null => {
  if (value === undefined || value === null) return null
  return Array.isArray(value) ? value : [value]
}

/** A URL atual já tem exatamente as chaves do Mapa desejadas (arrays comparados item a item). */
export function unitMapQueryMatches(
  current: Record<string, unknown>,
  desired: UnitMapSerializedQuery
): boolean {
  return UNIT_MAP_QUERY_KEYS.every((key) => {
    const a = asList(current[key])
    const b = asList(desired[key])
    if (a === null || b === null) return a === b
    return a.length === b.length && a.every((item, index) => item === b[index])
  })
}

export function stripUnitMapKeys<T extends Record<string, unknown>>(query: T): T {
  const copy: Record<string, unknown> = { ...query }
  UNIT_MAP_QUERY_KEYS.forEach((key) => delete copy[key])
  return copy as T
}
